import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Code, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { formatDate } from '@/lib/utils/date';
import { useTtsLogs } from '../api';
import type { TtsLogRow } from '../types';

/** Pretty-print a JSON string if possible; fall through to the raw text. */
function prettyJson(raw: string | undefined): string {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function TtsContLogsPage() {
  // Default to today's window (matches legacy startDate=today, endDate=today).
  const today = useMemo(() => new Date(), []);
  const [startDate, setStartDate] = useState<Date | undefined>(today);
  const [endDate, setEndDate] = useState<Date | undefined>(today);

  const startStr = startDate ? formatDate(startDate) : '';
  const endStr = endDate ? formatDate(endDate) : '';

  const logs = useTtsLogs(startStr, endStr);
  // Newest first by id desc — matches legacy default sort.
  const rows = useMemo(
    () => [...(logs.data ?? [])].sort((a, b) => b.id - a.id),
    [logs.data],
  );

  const [viewing, setViewing] = useState<TtsLogRow | null>(null);

  const columns = useMemo<ColumnDef<TtsLogRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 90,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.id}
          </span>
        ),
      },
      {
        accessorKey: 'campaignId',
        header: 'Campaign',
        size: 110,
        cell: ({ row }) => row.original.campaignId ?? '—',
      },
      {
        accessorKey: 'uniqueKey',
        header: 'Unique key',
        cell: ({ row }) => (
          <span className="truncate font-mono text-xs">
            {row.original.uniqueKey ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'requestDate',
        header: 'Requested',
        size: 180,
        cell: ({ row }) => row.original.requestDate ?? '—',
      },
      {
        accessorKey: 'totalNos',
        header: 'Total',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono">{row.original.totalNos ?? 0}</span>
        ),
      },
      {
        accessorKey: 'successCount',
        header: 'Success',
        size: 90,
        cell: ({ row }) => (
          <span className="font-mono text-emerald-700">
            {row.original.successCount ?? 0}
          </span>
        ),
      },
      {
        accessorKey: 'failedCount',
        header: 'Failed',
        size: 90,
        cell: ({ row }) => (
          <span className="font-mono text-rose-700">
            {row.original.failedCount ?? 0}
          </span>
        ),
      },
      {
        accessorKey: 'dndCount',
        header: 'DND',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-muted-foreground">
            {row.original.dndCount ?? 0}
          </span>
        ),
      },
      {
        id: 'payload',
        header: 'Payload',
        size: 110,
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setViewing(row.original)}
            disabled={!row.original.requestPayload}
          >
            <Code className="h-3.5 w-3.5" />
            View
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <FillPage>
      <PageHeader
        title="Run Logs"
        description="Continuous-campaign TTS run logs. Each row is one campaign batch — its counts and the JSON payload submitted to the runner."
        actions={
          <Button
            variant="outline"
            onClick={() => logs.refetch()}
            disabled={logs.isFetching}
          >
            <RefreshCw
              className={logs.isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
            />
            Refresh
          </Button>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        {/* Date filter bar */}
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3 shadow-sm">
          <div className="space-y-1.5">
            <Label className="text-xs">Start date</Label>
            <DatePicker value={startDate} onChange={setStartDate} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">End date</Label>
            <DatePicker value={endDate} onChange={setEndDate} />
          </div>
          <span className="ml-auto text-xs text-muted-foreground">
            {rows.length.toLocaleString()}{' '}
            {rows.length === 1 ? 'log entry' : 'log entries'}
          </span>
        </div>

        <div className="min-h-0 flex-1">
          <DataTable<TtsLogRow, unknown>
            tableId="tts-flow:logs:v1"
            columns={columns}
            data={rows}
            isLoading={logs.isLoading}
            searchPlaceholder="Search logs…"
            emptyTitle="No TTS logs"
            emptyDescription="No campaigns ran in this date range. Try widening the window."
            getRowId={(row) => String(row.id)}
            initialColumnPinning={{ right: ['payload'] }}
            exportFilename="tts-logs-cont.csv"
            pageSize={25}
          />
        </div>
      </div>

      {/* Payload viewer */}
      <Dialog
        open={viewing !== null}
        onOpenChange={(o) => !o && setViewing(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="h-4 w-4" />
              Payload — log #{viewing?.id}
            </DialogTitle>
            <DialogDescription>
              JSON the campaign runner submitted to the dialer.
            </DialogDescription>
          </DialogHeader>
          <pre className="max-h-[60vh] overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs leading-relaxed">
            {prettyJson(viewing?.requestPayload)}
          </pre>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
}
